import React, { useEffect, useMemo, useState } from 'react';
import { getClientCampaigns, type CampaignDetail } from '../../lib/refonteApi';
import { usePortalScope } from './portal/usePortalScope';
import ViewAsClientToggle from '../../components/refonte/ViewAsClientToggle';
import { Skeleton, SkeletonRows } from '../../components/refonte/Skeleton';
import EmptyState from '../../components/refonte/EmptyState';

const fmtD = (d?: string | null) => (d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' }) : '—');

const ChannelChip: React.FC<{ c: 'mail' | 'linkedin' }> = ({ c }) => (
  <span className={`font-mono text-[9px] px-1.5 py-0.5 rounded ${c === 'mail' ? 'bg-hook-rose/10 text-hook-rose' : 'bg-sky-500/10 text-sky-500'}`}>
    {c === 'mail' ? '✉️ Mail' : 'in LinkedIn'}
  </span>
);

const Kpi: React.FC<{ v: React.ReactNode; l: string; tone?: string }> = ({ v, l, tone }) => (
  <div className="bg-panel border border-line rounded-card p-4">
    <div className={`text-[24px] font-bold font-mono tabular-nums leading-none ${tone || ''}`}>{v}</div>
    <div className="font-mono text-[9px] uppercase tracking-widest text-faint mt-1">{l}</div>
  </div>
);

// Registre des campagnes (portail client) — pendant du registre des RDV, mais pour le mail
// et LinkedIn : chaque campagne, ses échanges (réponses), et les réponses positives.
// Données live (outreach_campaigns + outreach_replies). Volumes d'envoi & RDV liés = à venir.
const PortalCampagnesPage: React.FC = () => {
  const scope = usePortalScope();
  const { clientId, clientName } = scope;
  const [rows, setRows] = useState<CampaignDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [chan, setChan] = useState<'tous' | 'mail' | 'linkedin'>('tous');
  const [onlyPos, setOnlyPos] = useState(false);

  useEffect(() => {
    if (!clientId) { setLoading(false); return; }
    setLoading(true); setErr(null);
    getClientCampaigns(clientId).then(setRows).catch((e) => setErr(e.message)).finally(() => setLoading(false));
  }, [clientId]);

  const tot = useMemo(() => rows.reduce((a, r) => ({
    camp: a.camp + 1, rep: a.rep + r.replies, pos: a.pos + r.positives,
  }), { camp: 0, rep: 0, pos: 0 }), [rows]);

  const filtered = useMemo(() => rows.filter(r =>
    (chan === 'tous' || r.channel === chan) &&
    (!onlyPos || r.positives > 0) &&
    (!q || r.name.toLowerCase().includes(q.toLowerCase()))
  ), [rows, chan, onlyPos, q]);

  return (
    <div>
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-1">
        <h1 className="text-[21px] font-bold tracking-tight">Registre des campagnes</h1>
        <div className="flex items-center gap-2 flex-wrap">
          {scope.isInternal && <ViewAsClientToggle on={scope.viewAsClient} onChange={scope.setViewAsClient} />}
          {(scope.isInternal || scope.options.length > 1) && (
            <select value={clientId ?? ''} onChange={e => scope.setClientId(e.target.value || null)}
              className="rounded-md border border-line-2 bg-panel px-2 py-1.5 text-[13px] max-w-[220px]">
              {!clientId && <option value="">— choisir —</option>}
              {scope.options.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          )}
        </div>
      </div>
      <p className="text-dim text-[13px] mb-4">
        Vos campagnes mail & LinkedIn, leurs échanges et réponses positives.
        {clientName && <span className="text-faint"> · {clientName}</span>}
      </p>

      {(err || scope.error) && <EmptyState tone="error" title="Chargement impossible" hint={err || scope.error || ''} className="mb-4" />}

      {scope.resolving ? <Skeleton className="h-[60vh] rounded-card" /> : !clientId ? (
        <EmptyState title="Aucun client rattaché" hint="Votre compte n'est associé à aucun client." />
      ) : loading ? <SkeletonRows count={6} /> : rows.length === 0 ? (
        <EmptyState title="Aucune campagne" hint="Pas encore de campagne mail ou LinkedIn sur ce compte." />
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <Kpi v={tot.camp} l="Campagnes" />
            <Kpi v={tot.rep} l="Réponses reçues" />
            <Kpi v={tot.pos} l="Réponses positives" tone="text-hook-rose" />
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-3">
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher une campagne…" className="rounded-md border border-line-2 bg-panel px-2.5 py-1.5 text-[13px] w-60" />
            {([['tous', 'Tous'], ['mail', '✉️ Mail'], ['linkedin', 'in LinkedIn']] as const).map(([v, l]) => (
              <button key={v} onClick={() => setChan(v)} className={`text-[11.5px] font-semibold px-2.5 py-1 rounded-md border ${chan === v ? 'bg-ink text-white border-ink' : 'border-line-2 text-dim hover:border-ink'}`}>{l}</button>
            ))}
            <button onClick={() => setOnlyPos(v => !v)} className={`text-[11.5px] font-semibold px-2.5 py-1 rounded-md border ${onlyPos ? 'bg-hook-rose text-white border-hook-rose' : 'border-line-2 text-dim hover:border-ink'}`}>Positives seulement</button>
            <span className="ml-auto font-mono text-[11px] text-faint">{filtered.length} campagne{filtered.length > 1 ? 's' : ''}</span>
          </div>

          <div className="space-y-2">
            {filtered.map((c, i) => (
              <details key={c.campaignId + i} className="bg-panel border border-line rounded-card group">
                <summary className="cursor-pointer list-none px-4 py-3 flex items-center gap-3 hover:bg-panel-2 rounded-card">
                  <ChannelChip c={c.channel} />
                  <span className="text-[13.5px] font-semibold flex-1 min-w-0 truncate">{c.name}</span>
                  <span className="font-mono text-[11px] text-dim whitespace-nowrap">{c.replies} réponse{c.replies > 1 ? 's' : ''}</span>
                  {c.positives > 0 && <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-hook-rose/10 text-hook-rose whitespace-nowrap">{c.positives} positive{c.positives > 1 ? 's' : ''}</span>}
                  <span className="font-mono text-[10px] text-faint whitespace-nowrap hidden sm:inline">{fmtD(c.lastReplyAt)}</span>
                  <span className="font-mono text-[10px] text-faint group-open:rotate-90 transition-transform">›</span>
                </summary>
                <div className="px-4 pb-4 pt-1 border-t border-line">
                  {c.recentReplies.length === 0 ? (
                    <p className="text-[12px] text-faint py-2">Aucune réponse pour l'instant sur cette campagne.</p>
                  ) : (
                    <div className="space-y-2 mt-2 max-h-[46vh] overflow-y-auto -mx-1 px-1">
                      {c.recentReplies.map((r, j) => (
                        <div key={j} className={`rounded-md border p-2.5 ${r.positive ? 'border-hook-rose/30 bg-hook-rose/5' : 'border-line bg-raise/30'}`}>
                          <div className="flex items-center gap-2 text-[11.5px] mb-1">
                            {r.positive && <span className="font-mono text-[9px] px-1.5 py-0.5 rounded bg-hook-rose/15 text-hook-rose">positive</span>}
                            <span className="font-semibold truncate">{r.prospect || '—'}</span>
                            {r.company && <span className="text-faint truncate">· {r.company}</span>}
                            <span className="ml-auto font-mono text-[10px] text-faint shrink-0">{fmtD(r.at)}</span>
                          </div>
                          {r.message && <p className="text-[12px] text-dim leading-snug whitespace-pre-line">{r.message.slice(0, 500)}{r.message.length > 500 ? '…' : ''}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </details>
            ))}
          </div>

          <p className="font-mono text-[10px] text-faint mt-4">Données en direct. Volumes d'envoi, taux d'ouverture et RDV issus des campagnes : à venir.</p>
        </>
      )}
    </div>
  );
};

export default PortalCampagnesPage;
